import { useState, useCallback } from 'react';
import { messagesApi } from '../API/messagesApi';

export const useMessagesApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get recent messages for a conversation
  const getConversationMessages = useCallback(async (conversationId, count = 50) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.getConversationMessages(conversationId, count);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to get conversation messages');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get paginated messages for a conversation (for lazy loading)
  const getPaginatedMessages = useCallback(async (conversationId, skip = 0, take = 20) => {
    setError(null);
    try {
      const result = await messagesApi.getPaginatedMessages(conversationId, skip, take);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to get paginated messages');
      return null;
    }
  }, []);

  // Send a message as a consumer
  const sendConsumerMessage = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.sendConsumerMessage(data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to send consumer message');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Send a message as an operator
  const sendOperatorMessage = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.sendOperatorMessage(data);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to send operator message');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark a specific message as delivered
  const markMessageAsDelivered = useCallback(async (messageId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.markMessageAsDelivered(messageId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to mark message as delivered');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Mark all messages in a conversation as delivered
  const markAllMessagesAsDelivered = useCallback(async (conversationId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.markAllMessagesAsDelivered(conversationId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to mark all messages as delivered');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Get unread message count for a conversation
  const getUnreadMessageCount = useCallback(async (conversationId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await messagesApi.getUnreadMessageCount(conversationId);
      if (result.error) {
        setError(result.error);
        return null;
      }
      return result;
    } catch (err) {
      setError('Failed to get unread message count');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    loading,
    error,
    getConversationMessages,
    getPaginatedMessages,
    sendConsumerMessage,
    sendOperatorMessage,
    markMessageAsDelivered,
    markAllMessagesAsDelivered,
    getUnreadMessageCount,
  };
};
